import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { useAccountHistorySync } from '../../hooks/useAccountHistorySync';
import { useAlphalendEventSubscription } from '../../hooks/useAlphalendEventSubscription';
import { PieChart, formatValue, convertToKAndM } from 'ui-kit';
import { marketsArray } from '../../data/marketInfo';
import { getTokenIconUrl, getColorBySymbol, getTokenConfigBySymbol } from '../../utils/tokenUtils';
import SyncStatusIndicator from './SyncStatusIndicator';
import { formatDynamicCurrency } from '../../utils/formatUtils';
const SupplyAllocation = ({ positionCapId, portfolioSupplies, // NO default value - let null/undefined mean "loading"
 }) => {
    // Sync progress for status indicator (visual feedback only)
    const syncProgress = useAccountHistorySync(positionCapId);
    // Market data for latest prices via new subscription pattern
    const { data: marketHighFreqData } = useAlphalendEventSubscription('market_data', 'market_data_high_freq');
    const marketsLoading = !marketHighFreqData;
    // Portfolio is loading if undefined/null
    const isPortfolioLoading = portfolioSupplies === undefined || portfolioSupplies === null;
    // Full loading state: portfolio loading OR markets loading
    const isLoading = isPortfolioLoading || marketsLoading;
    // Create price map from market data (more accurate than portfolio prices)
    const tokenPrices = useMemo(() => {
        if (!marketHighFreqData)
            return {};
        const prices = {};
        Object.entries(marketHighFreqData.markets).forEach(([marketId, marketData]) => {
            const marketDetails = marketsArray.find((m) => m.id === marketId);
            if ((marketDetails === null || marketDetails === void 0 ? void 0 : marketDetails.symbol) && marketData.price) {
                const symbol = marketDetails.symbol.toUpperCase();
                prices[symbol] = parseFloat(marketData.price);
            }
        });
        return prices;
    }, [marketHighFreqData]);
    // Build chart data from portfolio (immediately available)
    const chartData = useMemo(() => {
        // During loading or no data, return empty array - PieChart handles skeleton
        if (isPortfolioLoading || !portfolioSupplies || portfolioSupplies.length === 0) {
            return [];
        }
        // Calculate total USD using best available prices
        const itemsWithUSD = portfolioSupplies.map((token) => {
            const tokenConfig = getTokenConfigBySymbol(token.symbol);
            // Prefer market price, fallback to portfolio price
            const currentPrice = tokenPrices[tokenConfig.symbol.toUpperCase()] || token.currentPrice;
            const valueInUSD = token.currentBalance * currentPrice;
            return {
                symbol: token.symbol,
                icon: token.icon || getTokenIconUrl(token.symbol),
                valueInUSD,
                currentBalance: token.currentBalance,
            };
        });
        const totalSupplied = itemsWithUSD.reduce((sum, item) => sum + item.valueInUSD, 0);
        return itemsWithUSD
            .map((item) => {
            const percentage = totalSupplied > 0 ? (item.valueInUSD / totalSupplied) * 100 : 0;
            const formattedAmount = item.valueInUSD < 0.001
                ? '< $0.001'
                : item.valueInUSD >= 0.01
                    ? formatValue(item.valueInUSD)
                    : '$' + convertToKAndM(item.valueInUSD, { decimals: 4 });
            const formattedPercentage = percentage < 0.1 ? '< 0.1%' : percentage.toFixed(percentage < 1 ? 2 : 1) + '%';
            return {
                name: item.symbol,
                value: percentage,
                amount: formattedAmount,
                displayPercentage: formattedPercentage,
                rawValue: item.valueInUSD,
                color: getColorBySymbol(item.symbol),
                icon: item.icon,
            };
        })
            .filter((item) => item.value > 0)
            .sort((a, b) => b.rawValue - a.rawValue);
    }, [portfolioSupplies, tokenPrices, isPortfolioLoading]);
    // Total balance from chart data
    const totalSuppliedValue = useMemo(() => {
        return chartData.reduce((sum, item) => sum + (item.rawValue || 0), 0);
    }, [chartData]);
    // Truly has no positions: NOT loading AND array is empty
    const hasNoPositions = !isPortfolioLoading && (portfolioSupplies === null || portfolioSupplies === void 0 ? void 0 : portfolioSupplies.length) === 0;
    return (_jsxs("div", { className: `theme-bg-lndPrimary sm:rounded-3xl rounded-xl lg:!p-12 px-5 py-6 shadow-sm`, children: [_jsxs("div", { className: "flex justify-between items-center", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("div", { className: "lg:text-2xl text-base font-InterSemiBold theme-text-lndPrimary", children: "Supply Details" }), !hasNoPositions && !isLoading && _jsx(SyncStatusIndicator, { syncProgress: syncProgress })] }), isLoading ? (_jsx(BalanceBadgeSkeleton, {})) : totalSuppliedValue > 0 ? (_jsxs("div", { className: "flex flex-row flex-nowrap items-center lg:gap-2 gap-1 theme-bg-lndTableInfo lg:rounded-xl rounded-md lg:px-4 px-2 lg:py-2 py-1", children: [_jsx("div", { className: "lg:text-sm text-xs theme-text-lndSecondary", children: "Balance" }), _jsxs("div", { className: "lg:text-base text-xs font-InterSemiBold theme-text-lndPrimary", children: ["$", formatDynamicCurrency(totalSuppliedValue)] })] })) : null] }), _jsx("div", { className: "flex w-full py-10", children: _jsx(PieChart, { data: chartData, isLoading: isLoading, showLegend: true, showTooltip: true, emptyStateText: "No supply positions found" // Shows only when !isLoading && data.length === 0
                 }) })] }));
};
const BalanceBadgeSkeleton = () => (_jsx("div", { className: "w-[86px] h-[24px] lg:w-[124px] lg:h-[40px] theme-bg-lndTableInfo lg:rounded-xl rounded-md animate-pulse" }));
export default React.memo(SupplyAllocation);
